import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId, DINOS } from '../data/dinos';
import { CommsBox, COMMS } from '../objects/CommsBox';

interface LevelClearedInit {
  dinoId: DinoId;
  score: number;
}

/**
 * Shown after a single-level (practice-mode) boss kill. Just the score and
 * two buttons — replay or back to picker. Nothing persists.
 */
export class LevelClearedScene extends Phaser.Scene {
  private payload!: LevelClearedInit;

  constructor() {
    super('LevelCleared');
  }

  init(data: LevelClearedInit) {
    this.payload = data;
  }

  create() {
    const { dinoId, score } = this.payload;
    const stats = DINOS[dinoId];
    this.cameras.main.setBackgroundColor(0x0d0a1a);

    // Stripe band so the planet color is unmistakable.
    this.add
      .rectangle(0, 0, GAME_WIDTH, 60, stats.stripe)
      .setOrigin(0, 0)
      .setAlpha(0.9);

    // Confetti starfield.
    for (let i = 0; i < 40; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(60, GAME_HEIGHT);
      this.add
        .image(x, y, 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.3, 0.8))
        .setTint(Math.random() < 0.3 ? stats.stripe : 0xffffff);
    }

    // Title.
    const title = this.add
      .text(GAME_WIDTH / 2, 30, `${stats.planet} CLEARED`, {
        fontFamily: '"VT323", monospace',
        fontSize: '20px',
        color: '#0d0a1a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: title,
      alpha: 1,
      scale: { from: 1.3, to: 1 },
      duration: 500,
      ease: 'Back.easeOut',
    });

    // Hero dino, idle.
    this.add
      .sprite(GAME_WIDTH / 2, 100, `dino-${dinoId}`)
      .setScale(4)
      .play(`${dinoId}-idle`);

    this.add
      .text(GAME_WIDTH / 2, 144, `${stats.name} stomped the boss`, {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0.85);

    // Score, animated count-up.
    const scoreText = this.add
      .text(GAME_WIDTH / 2, 174, '0000', {
        fontFamily: '"VT323", monospace',
        fontSize: '22px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const obj = { val: 0 };
    this.tweens.add({
      targets: obj,
      val: score,
      duration: Math.min(1200, Math.max(300, score)),
      ease: 'Cubic.easeOut',
      onUpdate: () => scoreText.setText(String(Math.round(obj.val)).padStart(4, '0')),
    });

    // Mika cheers, Mr Consequence seethes, then per-planet level-cleared
    // payoff. Three-line sequence keeps the rivalry alive between levels.
    const comms = new CommsBox(this);
    comms.saySequence([
      COMMS.bossDefeated(),
      COMMS.villainDefeated(dinoId),
      COMMS.levelCleared(dinoId),
    ]);

    // Buttons.
    this.makeButton(GAME_WIDTH / 2 - 60, GAME_HEIGHT - 24, 'AGAIN', stats.stripe, () =>
      this.scene.start('Game', { dinoId, mode: 'practice', runScore: 0 }),
    );
    this.makeButton(GAME_WIDTH / 2 + 60, GAME_HEIGHT - 24, 'LEVELS', 0x444466, () =>
      this.scene.start('CharacterSelect'),
    );

    // Keyboard.
    this.input.keyboard!.once('keydown-ENTER', () =>
      this.scene.start('Game', { dinoId, mode: 'practice', runScore: 0 }),
    );
    this.input.keyboard!.once('keydown-ESC', () => this.scene.start('CharacterSelect'));
    this.input.keyboard!.once('keydown-SPACE', () =>
      this.scene.start('Game', { dinoId, mode: 'practice', runScore: 0 }),
    );
  }

  private makeButton(x: number, y: number, label: string, color: number, onClick: () => void) {
    const w = 90;
    const h = 22;
    const bg = this.add.rectangle(x, y, w, h, color).setStrokeStyle(2, 0xffffff);
    this.add
      .text(x, y, label, {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setScale(1.05));
    bg.on('pointerout', () => bg.setScale(1));
    bg.on('pointerdown', onClick);
    return bg;
  }
}
